import { logger } from '../utils/logger';
import React, { useMemo } from 'react';
import { Stack, TextField, Button, Typography } from '@mui/material';
import {
  DEFAULT_IMPORT_SYNC_DAYS,
  ImportDateRange,
  getDefaultImportDateRange,
  getImportDateRangeError,
} from '../utils/importDateRange';

interface ImportDateRangeControlsProps {
  range: ImportDateRange;
  onChange: (next: ImportDateRange) => void;
  disabled?: boolean;
  size?: 'small' | 'medium';
  showPresets?: boolean;
  helperText?: string;
  errorText?: string | null;
}

const PRESET_OPTIONS = [
  { label: '30 days', days: 30 },
  { label: '90 days', days: 90 },
  { label: '180 days', days: 180 },
  { label: `${DEFAULT_IMPORT_SYNC_DAYS} days`, days: DEFAULT_IMPORT_SYNC_DAYS },
];

export const ImportDateRangeControls: React.FC<ImportDateRangeControlsProps> = ({
  range,
  onChange,
  disabled = false,
  size = 'small',
  showPresets = true,
  helperText = 'Leave unchanged to import the last 365 days.',
  errorText,
}) => {
  const derivedError = useMemo(() => errorText ?? getImportDateRangeError(range), [errorText, range]);

  const handleRangeChange = (key: keyof ImportDateRange) => (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange({
      ...range,
      [key]: event.target.value,
    });
  };

  const isPresetSelected = (days: number) => {
    if (!range.startDate || !range.endDate) {
      return false;
    }
    const presetRange = getDefaultImportDateRange(days);
    return presetRange.startDate === range.startDate && presetRange.endDate === range.endDate;
  };

  const helperColor = derivedError ? 'error.main' : 'text.secondary';

  return (
    <Stack spacing={1.5}>
      {showPresets && (
        <Stack direction="row" spacing={1} flexWrap="wrap">
          {PRESET_OPTIONS.map(({ label, days }) => (
            <Button
              key={label}
              size="small"
              variant={isPresetSelected(days) ? 'contained' : 'outlined'}
              onClick={() => onChange(getDefaultImportDateRange(days))}
              disabled={disabled}
            >
              {label}
            </Button>
          ))}
        </Stack>
      )}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextField
          label="Start date"
          type="date"
          size={size}
          value={range.startDate || ''}
          onChange={handleRangeChange('startDate')}
          InputLabelProps={{ shrink: true }}
          disabled={disabled}
          error={Boolean(derivedError)}
          helperText="Oldest transaction date"
          fullWidth
        />
        <TextField
          label="End date"
          type="date"
          size={size}
          value={range.endDate || ''}
          onChange={handleRangeChange('endDate')}
          InputLabelProps={{ shrink: true }}
          disabled={disabled}
          error={Boolean(derivedError)}
          helperText={derivedError || 'Most recent transaction date'}
          fullWidth
        />
      </Stack>
      <Typography variant="caption" sx={{ color: helperColor }}>
        {derivedError || helperText}
      </Typography>
    </Stack>
  );
};

export default ImportDateRangeControls;
